// features/auth/authSlice.js
import { createSlice, createAsyncThunk } from "@reduxjs/toolkit";
import * as authAPI from "./authApi";

import { logout as logoutApi } from "./authApi";

export const logoutUser = createAsyncThunk(
  "auth/logoutUser",
  async (_, thunkAPI) => {
    try {
      await logoutApi(); // Call backend logout
      return true;
    } catch (e) {
      return thunkAPI.rejectWithValue("Logout failed");
    }
  }
);
// Restore user safely from localStorage
let userFromStorage = null;
try {
  const rawUser = localStorage.getItem("user");
  if (rawUser && rawUser !== "undefined") {
    userFromStorage = JSON.parse(rawUser);
  }
} catch {
  localStorage.removeItem("user");
}

const initialState = {
  user: userFromStorage,
  token: localStorage.getItem("token") || null,
  loading: false,
  error: null,
};

// Signup thunk
export const signupUser = createAsyncThunk(
  "auth/signupUser",
  async (userData, thunkAPI) => {
    try {
      const response = await authAPI.signup(userData);
      return response.data;
    } catch (e) {
      return thunkAPI.rejectWithValue(
        e.response?.data?.detail || e.response?.data || "Signup failed"
      );
    }
  }
);

// Signin thunk
export const signinUser = createAsyncThunk(
  "auth/signinUser",
  async (credentials, thunkAPI) => {
    try {
      const response = await authAPI.signin(credentials);
      return response.data;
    } catch (e) {
      return thunkAPI.rejectWithValue(
        e.response?.data?.detail || e.response?.data || "Signin failed"
      );
    }
  }
);

const authSlice = createSlice({
  name: "auth",
  initialState,
  reducers: {
    setToken: (state, action) => {
      state.token = action.payload;
      if (action.payload) {
        localStorage.setItem("token", action.payload);
      } else {
        localStorage.removeItem("token");
      }
    },
    setUser: (state, action) => {
      state.user = action.payload;
      localStorage.setItem("user", JSON.stringify(state.user));
    },
    clearUser: (state) => {
      // only clear Redux state, not localStorage
      state.user = null;
      state.token = null;
    },
    signout: (state) => {
      // full logout, clears Redux state + localStorage
      state.user = null;
      state.token = null;
      localStorage.removeItem("user");
      localStorage.removeItem("token");
    },
  },
  extraReducers: (builder) => {
    builder
      // Signup
      .addCase(signupUser.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(signupUser.fulfilled, (state) => {
        state.loading = false;
      })
      .addCase(signupUser.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })

      // Signin
      .addCase(signinUser.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(signinUser.fulfilled, (state, action) => {
        state.loading = false;
        state.user = {
          id: action.payload.user_id,
          username: action.payload.username,
          role: action.payload.role,
          picture: action.payload.picture,
        };
        state.token = action.payload.access_token;
        localStorage.setItem("user", JSON.stringify(state.user));
        localStorage.setItem("token", state.token);
      })
      .addCase(signinUser.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })
      .addCase(logoutUser.fulfilled, (state) => {
        state.user = null;
        state.token = null;
        localStorage.removeItem("user");
        localStorage.removeItem("token");
      })
      .addCase(logoutUser.rejected, (state, action) => {
        console.error("Backend logout failed, forcing local logout:", action.payload);
        state.user = null;
        state.token = null;
        localStorage.removeItem("user");
        localStorage.removeItem("token");
      });
},
});

// ✅ Export actions
export const { setToken, setUser, clearUser, signout } = authSlice.actions;

// ✅ Export reducer
export default authSlice.reducer;
